import React, { useState, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { useTheme } from '@material-ui/core/styles';
import { useMediaQuery } from '@material-ui/core';

import { SCREEN_SIZE_CATEGORIES } from 'constants';
import {
  MobileAppValuesContext,
  NetworkStatusValuesContext,
  UserAgentValuesContext,
  ViewportValuesContext,
} from 'contexts';
import { useNetworkStatus } from 'hooks';
import { UserAgent } from 'types';
import { checkIsMobileApp, locationLogic } from 'utilities';

export const DeviceInfoProvider = ({ children }) => {
  // Network status
  const networkStatus = useNetworkStatus();
  const location = useLocation();

  // Only in development: ?simulateOffline=1 forces offline for testing without airplane mode.
  const searchString = locationLogic.getSearchStringFromLocation(location);
  const forceOfflineForTesting =
    process.env.NODE_ENV === 'development' &&
    new URLSearchParams(searchString).get('simulateOffline') === '1';

  const networkStatusValues = useMemo(
    () =>
      forceOfflineForTesting
        ? { ...networkStatus, isOnline: false, isOffline: true }
        : { ...networkStatus },
    [networkStatus, forceOfflineForTesting],
  );

  // Mobile app detection
  const [isMobileApp, setIsMobileApp] = useState(false);
  const [isMobileAppBookDownloaded, setIsMobileAppBookDownloaded] =
    useState(false);
  const [isMobileAppOffline, setIsMobileAppOffline] = useState(false);

  // This is a temporary solution to detect the new sync feature in the mobile app.
  // This can be removed once all mobile users have the new sync feature.
  const [isMobileAppWithNewSync, setIsMobileAppWithNewSync] = useState(false);

  useEffect(() => {
    const searchString = locationLogic.getSearchStringFromLocation(location);
    const searchParams = new URLSearchParams(searchString);
    setIsMobileApp(checkIsMobileApp(searchString));
    setIsMobileAppWithNewSync(searchParams.has('mobileAppWithNewSync', 'true'));
    setIsMobileAppBookDownloaded(
      searchParams.has('mobileAppBookDownloaded', 'true'),
    );
    setIsMobileAppOffline(searchParams.has('mobileAppOffline', 'true'));
  }, [location]);

  const mobileAppValues = useMemo(
    () => ({
      isMobileApp,
      isMobileAppWithNewSync,
      isMobileAppBookDownloaded,
      isMobileAppOffline,
    }),
    [
      isMobileApp,
      isMobileAppWithNewSync,
      isMobileAppBookDownloaded,
      isMobileAppOffline,
    ],
  );

  // User agent detection
  const [userAgent] = useState(() => new UserAgent(window.navigator.userAgent));

  const userAgentValues = useMemo(
    () => ({
      userAgent,
    }),
    [userAgent],
  );

  // Viewport measurements
  const [viewport, setViewport] = useState({});

  const hasWidthSmallerThanPhoneMaximum = useMediaQuery(
    useTheme().breakpoints.down('xs'),
  );

  const hasWidthSmallerThanTabletMaximum = useMediaQuery(
    useTheme().breakpoints.down('sm'),
  );

  const hasHeightSmallerThanPhoneMaximum = useMediaQuery(
    `(max-height: ${useTheme().breakpoints.values.sm - 1}px)`,
  );

  const hasHeightSmallerThanTabletMaximum = useMediaQuery(
    `(max-height: ${useTheme().breakpoints.values.md - 1}px)`,
  );

  const isLandscape = useMediaQuery(`(orientation:landscape)`);

  useEffect(
    function updateViewport() {
      let heightCategory, widthCategory;

      if (hasHeightSmallerThanPhoneMaximum) {
        heightCategory = SCREEN_SIZE_CATEGORIES.PHONE;
      } else if (hasHeightSmallerThanTabletMaximum) {
        heightCategory = SCREEN_SIZE_CATEGORIES.TABLET;
      } else {
        heightCategory = SCREEN_SIZE_CATEGORIES.DESKTOP;
      }

      if (hasWidthSmallerThanPhoneMaximum) {
        widthCategory = SCREEN_SIZE_CATEGORIES.PHONE;
      } else if (hasWidthSmallerThanTabletMaximum) {
        widthCategory = SCREEN_SIZE_CATEGORIES.TABLET;
      } else {
        widthCategory = SCREEN_SIZE_CATEGORIES.DESKTOP;
      }

      setViewport({
        widthCategory,
        heightCategory,
        isLandscape,
        isPortrait: !isLandscape,
      });
    },
    [
      isLandscape,
      hasHeightSmallerThanPhoneMaximum,
      hasHeightSmallerThanTabletMaximum,
      hasWidthSmallerThanPhoneMaximum,
      hasWidthSmallerThanTabletMaximum,
    ],
  );

  const viewportValues = useMemo(
    () => ({
      viewport,
    }),
    [viewport],
  );

  return (
    <UserAgentValuesContext.Provider value={userAgentValues}>
      <MobileAppValuesContext.Provider value={mobileAppValues}>
        <NetworkStatusValuesContext.Provider value={networkStatusValues}>
          <ViewportValuesContext.Provider value={viewportValues}>
            {children}
          </ViewportValuesContext.Provider>
        </NetworkStatusValuesContext.Provider>
      </MobileAppValuesContext.Provider>
    </UserAgentValuesContext.Provider>
  );
};

export default DeviceInfoProvider;
